import { motion, useTransform } from "framer-motion"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import hash from "@emotion/hash"

import { useConsole } from "contexts/Console"

import { grid } from "css/grid"
import { headline70, surtitle70, headline50, body50, light, bold } from "css/text"
import { contrastMode } from "css/a11y"
import getMediaQuery from "css/breakpoints"

import { useErsive } from "./context"
import PopinButton from "../popin-edito/PopinButton"
import CtaLink from "components/link/CtaLink"
import Link from "components/link/Link"
import { useNavigation } from "contexts/Navigation"

import { waTriggerEvent } from "components/analytics/DigitalDataLayer"

const gridVars = css`
  --grid-gap: 6px;
  --grid-col-num: 6;

  @media (min-aspect-ratio: 1/1) {
    --grid-gap: 8px;
    --grid-col-num: 12;
  }
`

export const txtAlignMobile = {
  start: `am${hash("txt-start")}`,
  center: `am${hash("txt-center")}`,
  end: `am${hash("txt-end")}`,
}

const TextsContainer = styled.div`
  ${gridVars}
  position: sticky;
  top: 100px;
  height: calc(100vh - 180px);
  grid-row: 1;
  grid-column: main;
  ${grid}
  grid-template-rows: 7vw [txt-start] auto [txt-end] 7vw;
  transform: translate3d(0, 0, 0);

  ${getMediaQuery("m")} {
    top: 0;
    height: 100vh;
  }
`

export const SubBlock = styled(motion.div)`
  position: absolute;
  grid-row: txt;

  &.${txtAlignMobile.start} {
    align-self: start;
  }
  &.${txtAlignMobile.center} {
    align-self: center;
  }
  &.${txtAlignMobile.end} {
    align-self: end;
  }

  grid-column: main;

  @media (min-aspect-ratio: 1/1) {
    &.align-ts,
    &.align-tc,
    &.align-te {
      align-self: start;
    }
    &.align-cs,
    &.align-cc,
    &.align-ce {
      align-self: center;
    }
    &.align-bs,
    &.align-bc,
    &.align-be {
      align-self: end;
    }

    &.align-ts,
    &.align-cs,
    &.align-bs {
      grid-column: col 1 / span 5;

      [dir="rtl"] & {
        grid-column: col 8 / span 5;
      }
    }

    &.align-tc,
    &.align-cc,
    &.align-bc {
      grid-column: col 4 / span 6;
    }

    &.align-te,
    &.align-ce,
    &.align-be {
      grid-column: col 8 / span 5;

      [dir="rtl"] & {
        grid-column: col 1 / span 5;
      }
    }
  }

  & > h3 {
    ${headline70}

    &:not(:last-child) {
      margin-block-end: 20px;
    }

    @media (prefers-contrast: no-preference) {
      &.gradient {
        background: var(--gradient);
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }
  }

  & > hgroup {
    display: grid;
    grid-template-rows: repeat(2, min-content);
    height: fit-content;

    & > h3 {
      ${headline70}
      grid-row-start: 2;
    }

    & > p {
      ${surtitle70}
      grid-row-start: 1;
      margin-block-end: 10px;
    }

    &:not(:last-child) {
      margin-block-end: 20px;
    }

    @media (prefers-contrast: no-preference) {
      &.gradient > h3,
      &.gradient > p {
        background: var(--gradient);
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }
  }

  &.without-margin h3 {
    margin-block-end: 0;
  }

  ${contrastMode}

  will-change: transform, opacity;
`

const ChapoPara = styled.p`
  ${headline50}

  &:not(:last-child) {
    margin-block-end: 20px;
  }

  &:lang(th) {
    padding-bottom: 0.5ex;
  }

  @media (prefers-contrast: no-preference) {
    &.gradient {
      background: var(--gradient);
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
`

const Extender = styled.div`
  --extend-margin: -1rem;
  ${getMediaQuery("m")} {
    --extend-margin: -2rem;
  }
  cursor: pointer;
  position: absolute;
  opacity: 0;
  left: var(--extend-margin);
  right: var(--extend-margin);
  top: var(--extend-margin);
  bottom: var(--extend-margin);

  & + a {
    position: relative;
    z-index: 1;
  }
`

const Para = styled.p`
  ${body50}
  ${light}

  &:not(:last-child) {
    margin-block-end: 30px;
  }

  b {
    ${bold}
  }
`

export function Heading({ kicker, title, subtitle, gradient }) {
  const colors = gradient?.colors ? gradient?.colors.map(({ color, opacity }) => `rgb(${color} / ${opacity})`) : []
  const gradientVal = colors.length ? `linear-gradient(${gradient.angle ? `${gradient.angle}deg, ` : ""}${colors})` : ""

  return !!title && !kicker && !subtitle ? (
    <h3 className={colors.length ? "gradient" : ""} style={{ "--gradient": gradientVal }}>
      {title}
    </h3>
  ) : !!title && (!!kicker || !!subtitle) ? (
    <hgroup className={colors.length ? "gradient" : ""} style={{ "--gradient": gradientVal }}>
      {kicker ? <p>{kicker}</p> : null}
      {title ? <h3>{title}</h3> : null}
      {subtitle ? <p>{subtitle}</p> : null}
    </hgroup>
  ) : null

  // return kicker || title || subtitle ? (
  //   <hgroup className={colors.length ? "gradient" : ""} style={{ "--gradient": gradientVal }}>
  //     {kicker ? <p>{kicker}</p> : null}
  //     {title ? <h3>{title}</h3> : null}
  //     {subtitle ? <p>{subtitle}</p> : null}
  //   </hgroup>
  // ) : null
}

export function Chapo({ txt, gradient }) {
  const colors = gradient?.colors ? gradient.colors.map(({ color, opacity }) => `rgb(${color} / ${opacity})`) : []
  const gradientVal = colors.length ? `linear-gradient(${gradient.angle ? `${gradient.angle}deg, ` : ""}${colors})` : ""

  return <ChapoPara className={colors.length ? "gradient" : ""} style={{ "--gradient": gradientVal }} dangerouslySetInnerHTML={{ __html: txt }} />
}

export function Paragraph({ txt }) {
  return <Para dangerouslySetInnerHTML={{ __html: txt }} />
}

function Block({ index, heading, chapo, chapo_gradient, paragraph, prog, align, align_mobile, amplitude, theme, opacity, popin_button, link, ...props }) {
  const console = useConsole()

  const { inTxt } = useErsive()
  const navigation = useNavigation()

  const outTxt = [opacity?.start || 0, 1, 1, opacity?.end || 0]

  const y = useTransform(prog, inTxt[index], [amplitude / 2, amplitude / 4, -amplitude / 4, -amplitude / 2])
  const txtopacity = useTransform(prog, inTxt[index], outTxt)
  const pointerEvents = useTransform(txtopacity, o => (o === 0 ? "none" : "all"))
  const txtTheme = theme?.theme || "dark-theme"
  const alignMobile = txtAlignMobile[align_mobile || "start"]

  const withoutMargin = !!heading.title && !!chapo ? "without-margin" : ""

  function triggerWa(a) {
    if (!a?.href?.includes("new-watches")) return
    const [family] = a.href.split("/").reverse()

    family &&
      waTriggerEvent({
        eventName: "newWatchesClicked",
        eventType: "Click",
        scrollersiveInfo: {
          familyName: family,
        },
      })
  }

  function onLink(e) {
    e.preventDefault()
    triggerWa(e.target)
    navigation.navigate(e.currentTarget.getAttribute("href"))
  }

  function onClick(e) {
    const tnes = e?.target?.nextElementSibling
    if (!tnes) return
    tnes.click()
  }

  return (
    <SubBlock className={`${align} ${txtTheme} ${alignMobile} ${withoutMargin}`} style={{ y, opacity: txtopacity, pointerEvents }}>
      <Heading {...heading} />
      {chapo ? <Chapo txt={chapo} gradient={chapo_gradient} /> : null}
      {paragraph.length ? paragraph.map((p, i) => <Paragraph key={`txt-block-paragraph-${i}`} txt={p} />) : null}
      {popin_button ? <PopinButton {...popin_button} /> : null}
      {link?.label ? (
        <>
          <Extender onClick={onClick} />
          <Link href={link.href} onClick={onLink} {...link}>
            {link.label}
          </Link>
        </>
      ) : null}
    </SubBlock>
  )
}

export default function TextBlock({ text_stop, prog }) {
  return (
    <TextsContainer>
      {text_stop.map((t, i) => (
        <Block key={`txt-block-${i}`} index={i} {...t} prog={prog} />
      ))}
    </TextsContainer>
  )
}
